/**
 * File name for the exported breakdown image:
 *   Breakdown - <Patient> - <Carrier> - <Policy holder> - <YYYY-MM-DD>.png
 *
 * Each part is made safe for every desktop file system and capped, the holder
 * is dropped when it is the patient (relation "self" or the same name), and the
 * whole base name is capped well under the 255-byte limits so a long carrier
 * or a double-barrelled family never breaks the download.
 */
import dayjs from "dayjs";

export const PART_MAX_CHARS = 40;
export const BASE_MAX_CHARS = 120;

// eslint-disable-next-line no-control-regex
const UNSAFE = /[\\/:*?"<>|\x00-\x1f\x7f]/g;

export function sanitizeFilePart(value, max = PART_MAX_CHARS) {
  let s = String(value == null ? "" : value)
    .replace(UNSAFE, "")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > max) s = s.slice(0, max).trim();
  // Trailing dots/spaces are illegal on Windows and look like a typo elsewhere.
  return s.replace(/[. ]+$/g, "");
}

function personName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim();
}

function samePerson(a, b) {
  return !!a && !!b && a.replace(/\s+/g, " ").toLowerCase() === b.replace(/\s+/g, " ").toLowerCase();
}

export function breakdownImageFilename({ patient, policy, exportedAt, ext = "png" } = {}) {
  const pt = patient || {};
  const pol = policy || {};
  const patientName = personName(pt.first_name, pt.last_name);
  const carrier = pol.insurance_name_display || pol.insurance_name || pol.carrier_display_name || "";
  const holder = personName(pol.first_name, pol.last_name);
  const relation = String(pol.relation_to_member || "").toLowerCase();
  const holderIsPatient = relation === "self" || samePerson(holder, patientName);

  const parts = ["Breakdown", patientName, carrier];
  if (holder && !holderIsPatient) parts.push(holder);
  const day = dayjs(exportedAt || undefined);
  parts.push(day.isValid() ? day.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"));

  let base = parts.map((p) => sanitizeFilePart(p)).filter(Boolean).join(" - ");
  if (base.length > BASE_MAX_CHARS) {
    base = base.slice(0, BASE_MAX_CHARS).replace(/[\s-]+$/g, "");
  }
  return `${base}.${ext}`;
}
